import React from "react";
import { Card } from "./ui";
import { UserIcon, TrendIcon, TrophyIcon, ShieldIcon, GlobeIcon } from "./ui/icons";

const FEATURES = [
  {
    icon: <UserIcon />,
    title: "Create your player profile",
    body: "Showcase your stats, achievements and playing history.",
  },
  {
    icon: <TrendIcon />,
    title: "Track ranking progress",
    body: "See your ranking evolve and compare with players around the world.",
  },
  {
    icon: <TrophyIcon />,
    title: "Enter tournaments worldwide",
    body: "Discover and join tournaments near you or across the globe.",
  },
];

const TRUST = [
  { icon: <ShieldIcon />, title: "Secure & Private", body: "Your data is protected" },
  { icon: <GlobeIcon />, title: "Global Community", body: "Players in 100+ countries" },
  { icon: <ShieldIcon />, title: "Trusted Platform", body: "Built for players, by players" },
];

// Shared split-screen shell for the auth screens: brand story on the left,
// the form card on the right.
const AuthSplit = ({ title, subtitle, children }) => (
  <div className="grid min-h-[calc(100vh-4.5rem)] lg:grid-cols-2">
    <div className="relative hidden flex-col justify-center overflow-hidden bg-gradient-to-br from-[#DCE9FF] via-[#EAF1FF] to-primary-wash px-8 py-16 lg:flex xl:px-16">
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full text-primary/10"
        viewBox="0 0 400 600"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        preserveAspectRatio="xMidYMid slice"
      >
        <circle cx="320" cy="120" r="140" />
        <circle cx="80" cy="480" r="110" />
        <path d="M200 0v600M0 300h400" />
      </svg>

      <div className="relative max-w-md">
        <h2 className="text-4xl font-bold leading-tight tracking-tight text-navy xl:text-5xl">
          Join the global
          <br />
          <span className="text-primary">padel community</span>
        </h2>
        <p className="mt-5 leading-relaxed text-navy/70">
          Track your rankings, compete in tournaments worldwide, and build your
          player profile. All in one place.
        </p>

        <div className="mt-10 space-y-3">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="flex gap-4 rounded-md border border-white/60 bg-white/70 p-4 backdrop-blur-sm"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-primary-wash text-primary">
                {feature.icon}
              </span>
              <div>
                <div className="text-sm font-semibold text-navy">
                  {feature.title}
                </div>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">
                  {feature.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
          {TRUST.map((item) => (
            <div key={item.title} className="flex items-center gap-2.5">
              <span className="text-primary">{item.icon}</span>
              <div>
                <div className="text-xs font-semibold text-navy">
                  {item.title}
                </div>
                <div className="text-[11px] text-muted">{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="flex items-center justify-center px-4 py-10 md:px-8 md:py-16">
      <Card elevated className="w-full max-w-md">
        <h1 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-sm text-muted">{subtitle}</p>}
        <div className="mt-7">{children}</div>
      </Card>
    </div>
  </div>
);

export default AuthSplit;
